/* FOM · cliente da API.

   Regras que este cliente respeita, e que nao sao opcionais:

   · toda escrita usa as ROTAS REAIS do backend — nao existe segundo mecanismo
     de persistencia, nem acesso direto ao banco;
   · todo POST de dominio manda Idempotency-Key;
   · RETRY REUSA A MESMA CHAVE. Gerar chave nova no retry anularia a protecao:
     o servidor veria dois pedidos diferentes e criaria dois registros;
   · 409 nunca vira sobrescrita — sobe como ConflitoError para a UI decidir;
   · o token vive em memoria; nada de credencial embutida no codigo. */
import type {
  FomNode, Relation, Organization, MapInfo, MapCounts, Fingerprint, HistoryEntry, Sessao,
  Investigation, InvestigationResumo, StatusClass, EvidenceType, ClosingEffect,
  Contract, ContractBody, AutomationLevel, PublicationResumo, Publication,
  BrainStatus, BrainProposta, BrainEvento, BrainExecucao, TipoDeNo,
} from './types.ts';
import type { PacoteGuia, ItemGuia, EntradaGuia } from '../dominio/guia-operacional.ts';

export class ApiError extends Error {
  readonly status: number;
  readonly codigo: string;
  readonly corpo: Record<string, unknown>;
  constructor(status: number, corpo: Record<string, unknown>) {
    super(String(corpo.message ?? corpo.error ?? `HTTP ${status}`));
    this.name = 'ApiError';
    this.status = status;
    this.codigo = String(corpo.error ?? 'erro');
    this.corpo = corpo;
  }
}

/* 409: o recurso mudou desde o estado que o cliente edita. Carrega a versao
   atual para que a UI possa recarregar sem adivinhar. */
export class ConflitoError extends ApiError {
  readonly atual: string | null;
  constructor(corpo: Record<string, unknown>) {
    super(409, corpo);
    this.name = 'ConflitoError';
    this.atual = typeof corpo.current_updated_at === 'string' ? corpo.current_updated_at : null;
  }
}

export type ApiDeps = {
  baseUrl?: string;
  fetch?: typeof globalThis.fetch;
  uuid?: () => string;
};

function novoUuid(): string {
  const c = globalThis.crypto;
  if (c && typeof c.randomUUID === 'function') return c.randomUUID();
  const hex = (n: number): string => n.toString(16);
  const r = (): number => Math.floor(Math.random() * 16);
  let saida = '';
  for (const ch of 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx') {
    if (ch === 'x') saida += hex(r());
    else if (ch === 'y') saida += hex((r() & 0x3) | 0x8);
    else saida += ch;
  }
  return saida;
}

type Opcoes = { corpo?: unknown; idempotencyKey?: string; semAuth?: boolean };

export function criarApi(deps: ApiDeps = {}) {
  const base = deps.baseUrl ?? '';
  const buscar = deps.fetch ?? globalThis.fetch.bind(globalThis);
  const gerarUuid = deps.uuid ?? novoUuid;

  let accessToken: string | null = null;
  let refreshToken: string | null = null;
  let aoPerderSessao: (() => void) | null = null;

  async function bruto(metodo: string, caminho: string, opcoes: Opcoes = {}): Promise<unknown> {
    const headers: Record<string, string> = { accept: 'application/json' };
    if (opcoes.corpo !== undefined) headers['content-type'] = 'application/json';
    if (!opcoes.semAuth && accessToken) headers.authorization = 'Bearer ' + accessToken;
    if (opcoes.idempotencyKey) headers['idempotency-key'] = opcoes.idempotencyKey;

    const resposta = await buscar(base + '/v1' + caminho, {
      method: metodo,
      headers,
      /* D-FOM-037: o refresh token vive num cookie `HttpOnly`, e cookie so
         acompanha a requisicao se o cliente pedir. Sem isto, renovar sessao
         deixaria de funcionar — e o codigo nao tem como suprir, porque ele nao
         consegue ler o cookie (que e exatamente o ponto). */
      credentials: 'include',
      body: opcoes.corpo === undefined ? undefined : JSON.stringify(opcoes.corpo),
    });

    if (resposta.status === 204) return null;

    const texto = await resposta.text();
    const corpo = texto ? (JSON.parse(texto) as Record<string, unknown>) : {};

    if (!resposta.ok) {
      if (resposta.status === 409) throw new ConflitoError(corpo);
      throw new ApiError(resposta.status, corpo);
    }
    return corpo;
  }

  /* O corpo vai VAZIO: quem carrega a credencial e o cookie. Mandar o token
     no corpo exigiria te-lo em memoria — e guardar refresh token em memoria de
     JavaScript e o problema que D-FOM-037 resolveu. */

  /* SINGLE-FLIGHT (FOM-ARQ-0059; causa raiz no FOM-ARQ-0057): o refresh e
     ROTATIVO e single-use no servidor — dois pedidos simultaneos com o mesmo
     cookie fazem o segundo apresentar um token ja queimado, o que derruba a
     FAMILIA inteira (reuse_detected) e desloga a pessoa no meio do trabalho.
     Como uma rajada de 401 e rotina (Promise.all no carregamento, mutacao +
     atualizarContexto), TODAS as chamadas concorrentes aguardam a MESMA
     promessa: so a primeira conversa com /auth/refresh. O `.finally` solta a
     promessa em sucesso OU falha — a proxima expiracao renova de novo, e uma
     falha nao deixa trava presa. Erros continuam subindo identicos para cada
     aguardante; quem trata continua sendo `pedir()`/quem chamou. */
  let renovacaoEmVoo: Promise<void> | null = null;

  function renovar(): Promise<void> {
    renovacaoEmVoo ??= (async () => {
      const r = (await bruto('POST', '/auth/refresh', {
        corpo: {},
        semAuth: true,
      })) as { access_token: string };
      accessToken = r.access_token;
    })().finally(() => { renovacaoEmVoo = null; });
    return renovacaoEmVoo;
  }

  /* Renova o access token uma unica vez por 401 e repete o pedido. Se o refresh
     tambem falhar, a sessao acabou — quem decide o que fazer e a camada de cima. */
  async function pedir(metodo: string, caminho: string, opcoes: Opcoes = {}): Promise<unknown> {
    try {
      return await bruto(metodo, caminho, opcoes);
    } catch (err) {
      const e = err as ApiError;
      /* Antes exigia `refreshToken` em memoria para tentar renovar. Agora a
         credencial esta no cookie e o codigo nao sabe se ela existe — entao
         tenta renovar e deixa o servidor responder. */
      if (e.status !== 401 || opcoes.semAuth) throw err;
      try {
        await renovar();
      } catch {
        accessToken = null;
        refreshToken = null;
        aoPerderSessao?.();
        throw err;
      }
      /* MESMA chave de idempotencia no retry — de proposito */
      return bruto(metodo, caminho, opcoes);
    }
  }

  const rota = (org: string, mapa: string): string =>
    '/orgs/' + org + '/maps/' + mapa;

  return {
    /* ---------------------------------------------------------- sessao --- */
    async login(email: string, senha: string): Promise<Sessao> {
      const r = (await bruto('POST', '/auth/login', {
        corpo: { email, password: senha }, semAuth: true,
      })) as Sessao;
      accessToken = r.access_token;
      /* Guardado apenas para clientes que NAO sao navegador (teste, script):
         no navegador o cookie e quem manda, e este campo nunca e persistido. */
      refreshToken = r.refresh_token;
      return r;
    },
    /* Retomar sessao apos F5: nao ha token para apresentar — pede-se
       renovacao e o navegador anexa o cookie. Se nao houver cookie valido, o
       servidor devolve 401 e a tela de login aparece. */
    async retomar(): Promise<void> {
      await renovar();
    },
    /* Sair tem de sair mesmo se o servidor recusar: o cookie e apagado pelo
       servidor, e o access token morre aqui. */
    async sair(): Promise<void> {
      await bruto('POST', '/auth/logout', { corpo: {}, semAuth: true })
        .catch(() => undefined);
      accessToken = null;
      refreshToken = null;
    },
    /* Quem sou eu — para o chip de usuario sobreviver ao F5 (o login traz o
       nome; a retomada por cookie nao traz nada). Rota que ja existia. */
    async me(): Promise<{ userId?: string; email?: string }> {
      const r = (await pedir('GET', '/auth/me')) as { user?: { userId?: string; email?: string } };
      return r.user ?? {};
    },

    /* -------------------------------------------- primeiro acesso ------ */

    /* A tela pergunta ao servidor se deve oferecer o primeiro acesso. A resposta
       e um booleano e nada mais — e se a pergunta falhar, a resposta e "nao":
       na duvida, a porta fica fechada. */
    async bootstrapDisponivel(): Promise<boolean> {
      try {
        const r = (await bruto('GET', '/auth/bootstrap', { semAuth: true })) as
          { available?: boolean };
        return r.available === true;
      } catch {
        return false;
      }
    },

    /* Cria a primeira identidade. NAO devolve sessao de proposito: criar conta e
       entrar sao coisas diferentes, e quem acabou de se cadastrar entra pela
       tela de login como todo mundo. */
    async criarPrimeiroAcesso(
      nome: string, email: string, senha: string, confirmacao: string,
    ): Promise<void> {
      await bruto('POST', '/auth/bootstrap', {
        semAuth: true,
        corpo: {
          name: nome,
          email,
          password: senha,
          password_confirmation: confirmacao,
        },
      });
    },

    get autenticado(): boolean { return accessToken !== null; },
    get refresh(): string | null { return refreshToken; },
    aoExpirar(fn: () => void): void { aoPerderSessao = fn; },

    /* ---------------------------------------------------------- leitura -- */
    async organizacoes(): Promise<Organization[]> {
      const r = (await pedir('GET', '/orgs')) as { organizations: Organization[] };
      return r.organizations;
    },
    async mapas(org: string): Promise<{ organization: Organization; maps: MapInfo[] }> {
      return (await pedir('GET', '/orgs/' + org + '/maps')) as
        { organization: Organization; maps: MapInfo[] };
    },
    async mapa(org: string, mapa: string): Promise<{ map: MapInfo; counts: MapCounts }> {
      return (await pedir('GET', '/orgs/' + org + '/maps/' + mapa)) as
        { map: MapInfo; counts: MapCounts };
    },
    /* `incluirRemovidos` traz tambem o que foi ocultado. O mapa nao usa isso;
       a tela de ocultos usa. Nunca e o padrao: quem pede o mapa quer o mapa. */
    async nos(
      org: string, mapa: string, opcoes: { incluirRemovidos?: boolean } = {},
    ): Promise<FomNode[]> {
      const sufixo = opcoes.incluirRemovidos ? '?include_removed=true' : '';
      const r = (await pedir('GET', rota(org, mapa) + '/nodes' + sufixo)) as
        { nodes: FomNode[] };
      return r.nodes;
    },
    async relacoes(org: string, mapa: string): Promise<Relation[]> {
      const r = (await pedir('GET', '/orgs/' + org + '/maps/' + mapa + '/relations')) as
        { relations: Relation[] };
      return r.relations;
    },
    async fingerprint(org: string, mapa: string): Promise<Fingerprint> {
      return (await pedir('GET', '/orgs/' + org + '/maps/' + mapa + '/fingerprint')) as Fingerprint;
    },
    async historico(org: string, mapa: string, limite = 30): Promise<HistoryEntry[]> {
      const r = (await pedir(
        'GET', '/orgs/' + org + '/maps/' + mapa + '/history?limit=' + String(limite),
      )) as { entries: HistoryEntry[] };
      return r.entries;
    },

    /* --------------------------------------------- guia operacional (0075A) --
       O guia da ORGANIZACAO da sessao. Ate o gate 0075A este conteudo vinha
       compilado no bundle e era escolhido por external_id, sem organizacao —
       o vazamento que o FOM-ARQ-0075 encontrou. Agora quem decide o que
       entregar e a API, pela organizacao do vinculo verificado. Organizacao
       sem guia recebe pacote vazio, nunca o guia de outra. */
    async guiaOperacional(org: string, mapa: string): Promise<PacoteGuia> {
      const r = (await pedir('GET', rota(org, mapa) + '/operational-guide')) as
        { aviso: string | null; fontes: ItemGuia[]; entradas: Record<string, EntradaGuia> };
      return { aviso: r.aviso, fontes: r.fontes ?? [], entradas: r.entradas ?? {} };
    },

    /* ---------------------------------------------------------- escrita -- */
    /* ------------------------------------------------- taxonomia (0073) --
       O catalogo de tipos visivel a esta organizacao: os 13 de sistema mais
       os que a casa criou. Quem valida escopo e existencia e o banco. */
    async tiposDeNo(org: string, mapa: string): Promise<TipoDeNo[]> {
      const r = (await pedir('GET', rota(org, mapa) + '/node-types')) as
        { node_types: TipoDeNo[] };
      return r.node_types;
    },
    async criarTipoDeNo(
      org: string, mapa: string, dados: { label: string; description?: string },
    ): Promise<TipoDeNo> {
      const r = (await pedir('POST', rota(org, mapa) + '/node-types', { corpo: dados })) as
        { node_type: TipoDeNo };
      return r.node_type;
    },
    async editarTipoDeNo(
      org: string, mapa: string, key: string,
      dados: { label?: string; description?: string; status?: 'active' | 'archived' },
    ): Promise<TipoDeNo> {
      const r = (await pedir('PATCH', rota(org, mapa) + '/node-types/' + key, { corpo: dados })) as
        { node_type: TipoDeNo };
      return r.node_type;
    },
    async criarNo(
      org: string, mapa: string,
      dados: {
        parent_external_id: string; name: string; st: string;
        node_type?: string; summary?: string; src?: string; child_kind?: string;
      },
      chave?: string,
    ): Promise<FomNode> {
      const r = (await pedir('POST', '/orgs/' + org + '/maps/' + mapa + '/nodes', {
        corpo: dados, idempotencyKey: chave ?? gerarUuid(),
      })) as { node: FomNode };
      return r.node;
    },

    async editarNo(
      org: string, mapa: string, externalId: string,
      dados: Record<string, unknown> & { expected_updated_at: string },
    ): Promise<FomNode> {
      const r = (await pedir(
        'PATCH', '/orgs/' + org + '/maps/' + mapa + '/nodes/' + externalId, { corpo: dados },
      )) as { node: FomNode };
      return r.node;
    },

    async removerNo(org: string, mapa: string, externalId: string): Promise<void> {
      await pedir('DELETE', '/orgs/' + org + '/maps/' + mapa + '/nodes/' + externalId);
    },

    async criarRelacao(
      org: string, mapa: string,
      dados: { from: string; to: string; type: string; kind: string; payload?: string },
      chave?: string,
    ): Promise<Relation> {
      const r = (await pedir('POST', '/orgs/' + org + '/maps/' + mapa + '/relations', {
        corpo: dados, idempotencyKey: chave ?? gerarUuid(),
      })) as { relation: Relation };
      return r.relation;
    },

    async removerRelacao(org: string, mapa: string, id: string): Promise<void> {
      await pedir('DELETE', '/orgs/' + org + '/maps/' + mapa + '/relations/' + id);
    },

    /* GATE 0062: editar relacao usa o PATCH que o backend JA tinha
       (FOM-ARQ-0061) — somente `type` e `payload`. As pontas (from/to) nao
       sao editaveis por decisao de dominio: corrigir ponta = remover + criar. */
    async editarRelacao(
      org: string, mapa: string, id: string,
      dados: { type?: string; payload?: string },
    ): Promise<Relation> {
      const r = (await pedir(
        'PATCH', '/orgs/' + org + '/maps/' + mapa + '/relations/' + id, { corpo: dados },
      )) as { relation: Relation };
      return r.relation;
    },

    /* ----------------------------------------------------- investigacao -- */
    /* Camada PRIVATE. `viewer` recebe 403 aqui, e isso e correto: a organizacao
       ja e conhecida, o que falta e nivel de acesso. */
    async investigacoes(org: string, mapa: string): Promise<InvestigationResumo[]> {
      const r = (await pedir('GET', rota(org, mapa) + '/investigations')) as
        { investigations: InvestigationResumo[] };
      return r.investigations;
    },

    async investigacao(org: string, mapa: string, code: string): Promise<Investigation> {
      const r = (await pedir('GET', rota(org, mapa) + '/investigations/' + code)) as
        { investigation: Investigation };
      return r.investigation;
    },

    /* O TETO. A tela chama isto para saber o que pode oferecer — nunca para
       decidir sozinha o que e valido. */
    async classificacoesPermitidas(
      org: string, mapa: string, code: string,
    ): Promise<StatusClass[]> {
      const r = (await pedir('GET', rota(org, mapa) + '/investigations/' + code + '/allowed')) as
        { allowed: StatusClass[] };
      return r.allowed;
    },

    async criarInvestigacao(
      org: string, mapa: string,
      dados: {
        node_external_id: string; question: string;
        needed?: string; origin_gap?: string; parent_code?: string;
      },
      chave?: string,
    ): Promise<Investigation> {
      const r = (await pedir('POST', rota(org, mapa) + '/investigations', {
        corpo: dados, idempotencyKey: chave ?? gerarUuid(),
      })) as { investigation: Investigation };
      return r.investigation;
    },

    async registrarEvidencia(
      org: string, mapa: string, code: string,
      dados: {
        type: EvidenceType; ref?: string; evidence_date?: string;
        description?: string; partial?: boolean;
      },
      chave?: string,
    ): Promise<Investigation> {
      const r = (await pedir(
        'POST', rota(org, mapa) + '/investigations/' + code + '/evidence',
        { corpo: dados, idempotencyKey: chave ?? gerarUuid() },
      )) as { investigation: Investigation };
      return r.investigation;
    },

    async responderInvestigacao(
      org: string, mapa: string, code: string, texto: string,
    ): Promise<Investigation> {
      const r = (await pedir(
        'POST', rota(org, mapa) + '/investigations/' + code + '/answer',
        { corpo: { text: texto } },
      )) as { investigation: Investigation };
      return r.investigation;
    },

    async encerrarInvestigacao(
      org: string, mapa: string, code: string,
      dados: { effect: ClosingEffect; to?: StatusClass; text?: string },
    ): Promise<Investigation> {
      const r = (await pedir(
        'POST', rota(org, mapa) + '/investigations/' + code + '/close', { corpo: dados },
      )) as { investigation: Investigation };
      return r.investigation;
    },

    /* --------------------------------------------------------- contrato -- */
    /* 404 aqui significa "este no ainda nao tem contrato", nao "erro". Quem
       chama distingue os dois. */
    async contrato(org: string, mapa: string, externalId: string): Promise<Contract> {
      const r = (await pedir('GET', rota(org, mapa) + '/nodes/' + externalId + '/contract')) as
        { contract: Contract };
      return r.contract;
    },

    /* PUT cria ou substitui — um contrato por no. Desfazer e gravar de novo o
       corpo anterior, e por isso `undo_of` existe: a trilha registra que
       aquela gravacao foi um desfazer, e nao uma edicao qualquer. */
    async gravarContrato(
      org: string, mapa: string, externalId: string,
      dados: {
        origem: StatusClass; automation_level?: AutomationLevel;
        automation_candidate?: boolean; body?: ContractBody; undo_of?: string;
      },
    ): Promise<Contract> {
      const r = (await pedir(
        'PUT', rota(org, mapa) + '/nodes/' + externalId + '/contract', { corpo: dados },
      )) as { contract: Contract };
      return r.contract;
    },

    async removerContrato(org: string, mapa: string, externalId: string): Promise<void> {
      await pedir('DELETE', rota(org, mapa) + '/nodes/' + externalId + '/contract');
    },

    /* ------------------------------------------------------- publicacao -- */
    async publicacoes(org: string, mapa: string): Promise<PublicationResumo[]> {
      const r = (await pedir('GET', rota(org, mapa) + '/publications')) as
        { publications: PublicationResumo[] };
      return r.publications;
    },

    async publicacao(org: string, mapa: string, versao: number): Promise<Publication> {
      const r = (await pedir('GET', rota(org, mapa) + '/publications/' + String(versao))) as
        { publication: Publication };
      return r.publication;
    },

    async publicado(org: string, mapa: string): Promise<Publication> {
      const r = (await pedir('GET', rota(org, mapa) + '/published')) as
        { publication: Publication };
      return r.publication;
    },

    /* So `owner` publica. A publicacao e imutavel e o snapshot ja sai sem a
       camada PRIVATE — o scrub e do banco, nao desta chamada. */
    async publicar(
      org: string, mapa: string,
      dados: { note?: string; from_version?: number } = {},
      chave?: string,
    ): Promise<{ publication: PublicationResumo }> {
      return (await pedir('POST', rota(org, mapa) + '/publish', {
        corpo: dados, idempotencyKey: chave ?? gerarUuid(),
      })) as { publication: PublicationResumo };
    },

    async fingerprintBaseOnly(
      org: string, mapa: string,
    ): Promise<{ fingerprint_base_only: string; nodes_walked: number }> {
      return (await pedir('GET', rota(org, mapa) + '/fingerprint/base-only')) as
        { fingerprint_base_only: string; nodes_walked: number };
    },

    /* -------------------------------------------------- posicao e no ----- */
    /* O backend chama a coluna de `node_ref`, nao `node_external_id`. O nome
       do dado manda; o cliente se adapta. */
    async posicoes(org: string, mapa: string): Promise<Record<string, { x: number; y: number }>> {
      const r = (await pedir('GET', rota(org, mapa) + '/positions')) as
        { positions: { node_ref: string; x: number; y: number }[] };
      const saida: Record<string, { x: number; y: number }> = {};
      for (const p of r.positions ?? []) saida[p.node_ref] = { x: p.x, y: p.y };
      return saida;
    },

    /* PUT idempotente por natureza (ON CONFLICT DO UPDATE): reenviar o mesmo
       corpo produz o mesmo estado, entao nao ha chave de idempotencia aqui. */
    async salvarPosicoes(
      org: string, mapa: string,
      posicoes: { node_ref: string; x: number; y: number }[],
    ): Promise<void> {
      await pedir('PUT', rota(org, mapa) + '/positions', { corpo: { positions: posicoes } });
    },

    /* Duas restauracoes DIFERENTES, e confundi-las seria grave:

         'visibility' — desfaz a remocao logica. O no volta a aparecer COM as
                        edicoes que tinha.
         'base'       — descarta as edicoes e traz de volta o que a BASE dizia.
                        So vale para no de origem `base`, e so funciona porque
                        `base_payload` e imutavel (I-1).

       Quem chama escolhe; o cliente nao adivinha. */
    async restaurarNo(
      org: string, mapa: string, externalId: string,
      escopo: 'visibility' | 'base',
    ): Promise<FomNode> {
      const r = (await pedir(
        'POST', rota(org, mapa) + '/nodes/' + externalId + '/restore', { corpo: { scope: escopo } },
      )) as { node: FomNode };
      return r.node;
    },

    /* Mover tambem e escrita concorrente: exige `expected_updated_at`, e um
       409 aqui significa que alguem moveu antes — nao que a operacao falhou. */
    async moverNo(
      org: string, mapa: string, externalId: string,
      dados: {
        parent_external_id: string; expected_updated_at: string;
        child_kind?: 'kids' | 'flow'; ordinal?: number;
      },
    ): Promise<FomNode> {
      const r = (await pedir(
        'POST', rota(org, mapa) + '/nodes/' + externalId + '/move', { corpo: dados },
      )) as { node: FomNode };
      return r.node;
    },

    /* ---------------------------------------------------- Operating Brain -- */
    /* Camada PRIVATE: viewer recebe 403 em tudo daqui. */

    async brainStatus(org: string, mapa: string): Promise<BrainStatus> {
      return (await pedir('GET', rota(org, mapa) + '/brain/status')) as BrainStatus;
    },

    /* Dispara UMA analise. A chave de idempotencia importa aqui mais do que em
       qualquer outro lugar: sem ela, dois cliques viram duas analises e o dobro
       de propostas repetidas. */
    async brainAnalisar(org: string, mapa: string, chave?: string): Promise<BrainExecucao> {
      return (await pedir('POST', rota(org, mapa) + '/brain/runs', {
        corpo: {}, idempotencyKey: chave ?? gerarUuid(),
      })) as BrainExecucao;
    },

    async brainPropostas(
      org: string, mapa: string, status?: string,
    ): Promise<BrainProposta[]> {
      const sufixo = status ? '?status=' + status : '';
      const r = (await pedir('GET', rota(org, mapa) + '/brain/proposals' + sufixo)) as
        { proposals: BrainProposta[] };
      return r.proposals;
    },

    async brainProposta(org: string, mapa: string, code: string): Promise<BrainProposta> {
      const r = (await pedir('GET', rota(org, mapa) + '/brain/proposals/' + code)) as
        { proposal: BrainProposta };
      return r.proposal;
    },

    /* Aprovar E executar sao o mesmo ato: quem aprova assume a acao. */
    async brainAprovar(
      org: string, mapa: string, code: string, nota?: string, chave?: string,
    ): Promise<{ proposal: BrainProposta; result: Record<string, unknown> }> {
      return (await pedir(
        'POST', rota(org, mapa) + '/brain/proposals/' + code + '/approve',
        { corpo: nota ? { note: nota } : {}, idempotencyKey: chave ?? gerarUuid() },
      )) as { proposal: BrainProposta; result: Record<string, unknown> };
    },

    async brainRecusar(
      org: string, mapa: string, code: string, nota?: string,
    ): Promise<BrainProposta> {
      const r = (await pedir(
        'POST', rota(org, mapa) + '/brain/proposals/' + code + '/reject',
        { corpo: nota ? { note: nota } : {} },
      )) as { proposal: BrainProposta };
      return r.proposal;
    },

    async brainEventos(org: string, mapa: string): Promise<BrainEvento[]> {
      const r = (await pedir('GET', rota(org, mapa) + '/brain/events')) as
        { events: BrainEvento[] };
      return r.events;
    },

    novaChave: gerarUuid,
  };
}

export type Api = ReturnType<typeof criarApi>;
